// frontend/src/utils/functionRegistry.ts
// SINGLE SOURCE OF TRUTH – All functions defined here with modern style

export type ParameterKind =
  | { type: 'number' }
  | { type: 'string' }
  | { type: 'series' }
  | { type: 'seriesDate' }
  | { type: 'seriesAware'; requiredFields: string[]; defaultLookback?: number }
  | { type: 'seriesAwareWindow'; requiredFields: string[]; lookbackBars: number }
  | { type: 'literal'; defaultValue?: number | string | boolean };

export type FunctionDefinition = {
  label: string;
  symbol: string;
  category: 'operator' | 'function';
  group: string;
  parameters: ParameterKind[];
  renderStyle: 'infix' | 'prefix' | 'arithmetic';
  returnType?: 'number' | 'string';
  enumValues?: string[];
  operandLabels?: string[];
  minParameters: number;         // required – explicit min arity
  maxParameters: number | null;  // required – explicit max arity (null = unlimited)
};

export const functionRegistry = new Map<string, FunctionDefinition>();

// ===================================================================
// LOGICAL & COMPARISON
// ===================================================================
functionRegistry.set('gt', {
  label: '>',
  symbol: '>',
  category: 'operator',
  group: 'Comparison',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'series' }, { type: 'series' }],
  minParameters: 2,
  maxParameters: 2,
});

functionRegistry.set('lt', {
  label: '<',
  symbol: '<',
  category: 'operator',
  group: 'Comparison',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'series' }, { type: 'series' }],
  minParameters: 2,
  maxParameters: 2,
});

functionRegistry.set('gte', {
  label: '>=',
  symbol: '>=',
  category: 'operator',
  group: 'Comparison',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'series' }, { type: 'series' }],
  minParameters: 2,
  maxParameters: 2,
});

functionRegistry.set('lte', {
  label: '<=',
  symbol: '<=',
  category: 'operator',
  group: 'Comparison',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'series' }, { type: 'series' }],
  minParameters: 2,
  maxParameters: 2,
});

functionRegistry.set('=', {
  label: '=',
  symbol: '=',
  category: 'operator',
  group: 'Comparison',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'series' }, { type: 'series' }],
  minParameters: 2,
  maxParameters: 2,
});

functionRegistry.set('!=', {
  label: '≠',
  symbol: '≠',
  category: 'operator',
  group: 'Comparison',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'series' }, { type: 'series' }],
  minParameters: 2,
  maxParameters: 2,
});

functionRegistry.set('and', {
  label: 'AND',
  symbol: 'AND',
  category: 'operator',
  group: 'Logical',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'series' }, { type: 'series' }],
  minParameters: 2,
  maxParameters: null,
});

functionRegistry.set('or', {
  label: 'OR',
  symbol: 'OR',
  category: 'operator',
  group: 'Logical',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'series' }, { type: 'series' }],
  minParameters: 2,
  maxParameters: null,
});

functionRegistry.set('not', {
  label: 'NOT',
  symbol: 'NOT',
  category: 'operator',
  group: 'Logical',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'series' }],
  minParameters: 1,
  maxParameters: 1,
});

// ===================================================================
// MATH
// ===================================================================
functionRegistry.set('add', {
  label: '+',
  symbol: '+',
  category: 'operator',
  group: 'Arithmetic',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'series' }, { type: 'series' }],
  minParameters: 2,
  maxParameters: null,
});

functionRegistry.set('multiply', {
  label: '×',
  symbol: '×',
  category: 'operator',
  group: 'Arithmetic',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'series' }, { type: 'series' }],
  minParameters: 2,
  maxParameters: null,
});

functionRegistry.set('divide', {
  label: '÷',
  symbol: '÷',
  category: 'operator',
  group: 'Arithmetic',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'series' }, { type: 'series' }],
  minParameters: 2,
  maxParameters: 2,
});

functionRegistry.set('avg', {
  label: 'Average',
  symbol: 'AVG',
  category: 'function',
  group: 'Aggregation',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'series' }, { type: 'series' }],
  minParameters: 2,
  maxParameters: null,
});

functionRegistry.set('roc', {
  label: 'Rate of Change',
  symbol: 'ROC',
  category: 'function',
  group: 'Momentum',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [
    { type: 'series' },
    { type: 'series' },
  ],
  minParameters: 2,
  maxParameters: 2,
});

// ===================================================================
// MISC
// ===================================================================
functionRegistry.set('DayOfWeek', {
  label: 'Day of Week',
  symbol: 'DayOfWeek',
  category: 'function',
  group: 'Date & Time',
  renderStyle: 'prefix',
  returnType: 'string',
  enumValues: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  parameters: [{ type: 'seriesDate' }],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('quarter', {
  label: 'Quarter',
  symbol: 'Quarter',
  category: 'function',
  group: 'Date & Time',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'seriesDate' }],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('days_until_eom', {
  label: 'Days Until EOM',
  symbol: 'Days Until EOM',
  category: 'function',
  group: 'Date & Time',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'seriesDate' }],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('days_until_eoq', {
  label: 'Days Until EOQ',
  symbol: 'Days Until EOQ',
  category: 'function',
  group: 'Date & Time',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'seriesDate' }],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('days_since_som', {
  label: 'Days Since SOM',
  symbol: 'Days Since SOM',
  category: 'function',
  group: 'Date & Time',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'seriesDate' }],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('days_until_eoy', {
  label: 'Days Until EOY',
  symbol: 'Days Until EOY',
  category: 'function',
  group: 'Date & Time',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'seriesDate' }],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('days_since_boy', {
  label: 'Days Since BOY',
  symbol: 'Days Since BOY',
  category: 'function',
  group: 'Date & Time',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'seriesDate' }],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('days_until_mon_opex', {
  label: 'Days until Mon. Opex',
  symbol: 'Days until Mon. Opex',
  category: 'function',
  group: 'Date & Time',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'seriesDate' }],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('days_since_mon_opex', {
  label: 'Days Since Mon. Opex',
  symbol: 'Days Since Mon. Opex',
  category: 'function',
  group: 'Date & Time',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'seriesDate' }],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('days_until_nfp', {
  label: 'Days until NFP',
  symbol: 'Days until NFP',
  category: 'function',
  group: 'Date & Time',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'seriesDate' }],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('days_since_nfp', {
  label: 'Days Since NFP',
  symbol: 'Days Since NFP',
  category: 'function',
  group: 'Date & Time',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'seriesDate' }],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('days_until_fomc', {
  label: 'Days until FOMC',
  symbol: 'Days until FOMC',
  category: 'function',
  group: 'Date & Time',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'seriesDate' }],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('days_since_fomc', {
  label: 'Days Since FOMC',
  symbol: 'Days Since FOMC',
  category: 'function',
  group: 'Date & Time',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [{ type: 'seriesDate' }],
  minParameters: 1,
  maxParameters: 1,
});

// ===================================================================
// NEW SERIES-AWARE PIVOT & TREND FUNCTIONS
// ===================================================================
functionRegistry.set('pivot_point', {
  label: 'Pivot Point',
  symbol: 'PP',
  category: 'function',
  group: 'Pivot Points',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [
    { type: 'seriesAware', requiredFields: ['high', 'low', 'close'], defaultLookback: 1 },
  ],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('r1', {
  label: 'R1',
  symbol: 'R1',
  category: 'function',
  group: 'Pivot Points',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [
    { type: 'seriesAware', requiredFields: ['high', 'low', 'close'], defaultLookback: 1 },
  ],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('r2', {
  label: 'R2',
  symbol: 'R2',
  category: 'function',
  group: 'Pivot Points',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [
    { type: 'seriesAware', requiredFields: ['high', 'low', 'close'], defaultLookback: 1 },
  ],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('r3', {
  label: 'R3 (Camarilla)',
  symbol: 'R3',
  category: 'function',
  group: 'Pivot Points',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [
    { type: 'seriesAware', requiredFields: ['high', 'low', 'close'], defaultLookback: 1 },
  ],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('s1', {
  label: 'S1',
  symbol: 'S1',
  category: 'function',
  group: 'Pivot Points',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [
    { type: 'seriesAware', requiredFields: ['high', 'low', 'close'], defaultLookback: 1 },
  ],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('s2', {
  label: 'S2',
  symbol: 'S2',
  category: 'function',
  group: 'Pivot Points',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [
    { type: 'seriesAware', requiredFields: ['high', 'low', 'close'], defaultLookback: 1 },
  ],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('s3', {
  label: 'S3 (Camarilla)',
  symbol: 'S3',
  category: 'function',
  group: 'Pivot Points',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [
    { type: 'seriesAware', requiredFields: ['high', 'low', 'close'], defaultLookback: 1 },
  ],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('is_trend_buy', {
  label: 'Is Trend Buy Day',
  symbol: 'TrendBuy',
  category: 'function',
  group: 'Trend',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [
    { type: 'seriesAware', requiredFields: ['open', 'high', 'low', 'close'], defaultLookback: 1 },
    { type: 'literal', defaultValue: 0.2 },
  ],
  minParameters: 2,
  maxParameters: 2,
});

functionRegistry.set('is_trend_sell', {
  label: 'Is Trend Sell Day',
  symbol: 'TrendSell',
  category: 'function',
  group: 'Trend',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [
    { type: 'seriesAware', requiredFields: ['open', 'high', 'low', 'close'], defaultLookback: 1 },
    { type: 'literal', defaultValue: 0.2 },
  ],
  minParameters: 2,
  maxParameters: 2,
});

// NR4: Narrow Range 4 (Crabel) – current bar range < min of previous 3 bars' ranges
functionRegistry.set('nr4', {
  label: 'Is NR4 (Narrow Range 4)',
  symbol: 'NR4',
  category: 'function',
  group: 'Patterns',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [
    { type: 'seriesAwareWindow', requiredFields: ['high', 'low'], lookbackBars: 4 },
  ],
  minParameters: 1,
  maxParameters: 1,
});

// Bull Hook: gap up open, close below prior close, today range < prior range (bullish setup)
functionRegistry.set('bull_hook', {
  label: 'Is Bull Hook',
  symbol: 'Bull Hook',
  category: 'function',
  group: 'Patterns',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [
    { type: 'seriesAwareWindow', requiredFields: ['open', 'high', 'low', 'close'], lookbackBars: 2 },
  ],
  minParameters: 1,
  maxParameters: 1,
});

// Inside Day: today's range fully inside prior (high_0 < high_1, low_0 > low_1)
functionRegistry.set('is_inside_day', {
  label: 'Is Inside Day',
  symbol: 'Is Inside Day',
  category: 'function',
  group: 'Patterns',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [
    { type: 'seriesAwareWindow', requiredFields: ['high', 'low'], lookbackBars: 2 },
  ],
  minParameters: 1,
  maxParameters: 1,
});

// Outside Day: today's range fully engulfs prior (high_0 > high_1, low_0 < low_1)
functionRegistry.set('is_outside_day', {
  label: 'Is Outside Day',
  symbol: 'Is Outside Day',
  category: 'function',
  group: 'Patterns',
  renderStyle: 'prefix',
  returnType: 'number',
  parameters: [
    { type: 'seriesAwareWindow', requiredFields: ['high', 'low'], lookbackBars: 2 },
  ],
  minParameters: 1,
  maxParameters: 1,
});

// Gap classification: all use 2-bar OHLC window (today + prior)
const gapWindowParam = { type: 'seriesAwareWindow' as const, requiredFields: ['open', 'high', 'low', 'close'], lookbackBars: 2 };
const gapDef = (label: string, symbol: string) => ({
  label,
  symbol,
  category: 'function' as const,
  group: 'Gaps' as const,
  renderStyle: 'prefix' as const,
  returnType: 'number' as const,
  parameters: [gapWindowParam],
  minParameters: 1,
  maxParameters: 1,
});

functionRegistry.set('is_gap_up', gapDef('Is Gap Up', 'Is Gap Up'));
functionRegistry.set('is_full_gap_up', gapDef('Is Full Gap Up', 'Is Full Gap Up'));
functionRegistry.set('is_partial_gap_up', gapDef('Is Partial Gap Up', 'Is Partial Gap Up'));
functionRegistry.set('is_filled_full_gap_up', gapDef('Is Filled Full Gap Up', 'Is Filled Full Gap Up'));
functionRegistry.set('is_filled_partial_gap_up', gapDef('Is Filled Partial Gap Up', 'Is Filled Partial Gap Up'));
functionRegistry.set('is_unfilled_full_gap_up', gapDef('Is Unfilled Full Gap Up', 'Is Unfilled Full Gap Up'));
functionRegistry.set('is_unfilled_partial_gap_up', gapDef('Is Unfilled Partial Gap Up', 'Is Unfilled Partial Gap Up'));

functionRegistry.set('is_gap_down', gapDef('Is Gap Down', 'Is Gap Down'));
functionRegistry.set('is_full_gap_down', gapDef('Is Full Gap Down', 'Is Full Gap Down'));
functionRegistry.set('is_partial_gap_down', gapDef('Is Partial Gap Down', 'Is Partial Gap Down'));
functionRegistry.set('is_filled_full_gap_down', gapDef('Is Filled Full Gap Down', 'Is Filled Full Gap Down'));
functionRegistry.set('is_filled_partial_gap_down', gapDef('Is Filled Partial Gap Down', 'Is Filled Partial Gap Down'));
functionRegistry.set('is_unfilled_full_gap_down', gapDef('Is Unfilled Full Gap Down', 'Is Unfilled Full Gap Down'));
functionRegistry.set('is_unfilled_partial_gap_down', gapDef('Is Unfilled Partial Gap Down', 'Is Unfilled Partial Gap Down'));